import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CommitOnEnterDirective } from 'src/app/shared/directives/commit-on-enter.directive';
import { SelectOnFocusDirective } from 'src/app/shared/directives/select-on-focus.directive';
import { TwoDecimalsDirective } from 'src/app/shared/directives/two-decimals.directive';
import { ReallocationV2Store } from '../../state/reallocation-v2.store';

/**
 * Step 2 of a Contribution or Distribution — how much of the funding amount each existing model
 * sleeve takes or gives up.
 *
 * **One component, two modes.** The table, the arithmetic and the gate are identical; what
 * differs is the direction of the money, which changes the copy, which Orion percentage column
 * is shown, and whether a row can exceed the sleeve's value. Two components would have been two
 * copies of the same running total.
 *
 * Deliberately NOT the allocation grid. That one states a target allocation — three
 * interdependent percentage columns totalling 100, with rows the advisor can add and remove.
 * This states dollars against a fixed set of sleeves, and its total has to land on the funding
 * amount rather than on 100%.
 */
@Component({
  selector: 'app-rav2-sleeve-amount-step',
  standalone: true,
  imports: [CommonModule, SelectOnFocusDirective, CommitOnEnterDirective, TwoDecimalsDirective],
  templateUrl: './sleeve-amount-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SleeveAmountStepComponent {
  readonly store = inject(ReallocationV2Store);

  // 🔴 `heading()` LIVED HERE AND IS GONE (AndyP, 2026-09-25). The shell already prints the
  // step's name above the body, so this rendered it a second time -- identically on a
  // contribution, and as "Distribute from Models" under the shell's "Allocate to Models" on a
  // distribution, which is the same duplication wearing a disguise.
  //
  // ⚠️ THE TYPE RULE IT CARRIED IS NOT LOST. `stepLabelOverrides` in the shell already maps
  // this step per type -- "Allocate to Models" on a contribution, "Raise Cash from Models" on a
  // distribution -- and `currentStepLabel` now reads it, which it previously did not.

  lede(): string {
    return this.store.isContribution()
      ? 'Enter how much of the contribution each model should receive. Anything left over can be '
        + 'used to buy securities or set aside as cash.'
      : 'Specify how much of the distribution should come from each model. Any remaining amount '
        + 'can be sourced from Custom Holdings in the next step.';
  }

  /** Header for the read-only Orion column, which is a different instruction per type. */
  orionColumnHeading(): string {
    return this.store.isContribution() ? 'Contribution %' : 'Distribution %';
  }

  /**
   * Why the Orion column is read-only, as a tooltip.
   *
   * Worth saying explicitly: it looks like an editable percentage next to an editable amount,
   * and an advisor could reasonably expect typing in it to re-split the money. It is the
   * account's STANDING instruction, which this request does not change — that would be a
   * reallocation.
   */
  orionColumnTooltip(): string {
    const kind = this.store.isContribution() ? 'contributions' : 'distributions';
    return `Orion's standing split for ${kind} on this account. This request does not change it.`;
  }

  /** An amount for its input box — blank at zero, matching the allocation grid. */
  amountInput(value: number): string {
    return value === 0 ? '' : value.toFixed(2);
  }

  onAmount(sleeveKey: string, event: Event): void {
    const raw = (event.target as HTMLInputElement).value.replace(/,/g, '');
    const parsed = parseFloat(raw);
    // An emptied box means nothing from this sleeve, not "work it out for me". Zero is the
    // honest reading, and it leaves the money outstanding so the gate can report it.
    this.store.editSleeveAmount(sleeveKey, raw === '' || isNaN(parsed) ? 0 : parsed);
  }

  /**
   * What the quick-apply button says.
   *
   * Names the AMOUNT, so the advisor sees what the click will do before making it, and names the
   * DIRECTION, because the same control both places an outstanding balance and takes back an
   * over-commitment. Mirrors `remainingLabel()` on the reallocation grid.
   *
   * ⚠️ NO LONGER SPLIT BY TYPE (AndyP, 2026-09-30). A distribution said 'Distribute remaining'
   * against the contribution's 'Allocate remaining', and it named the wrong act: the press spreads
   * the outstanding balance across the boxes on this step, which is allocation on either type. The
   * distributing happens at the end of the request, not here. Both branches returned to the same
   * string, so the ternary went with them.
   */
  applyLabel(): string {
    // ⚠️ The CAPPED figure, so the label cannot name more than the press will move -- see
    // `sleeveApplyAmount`. On a distribution `unappliedValue` includes what the next step raised.
    const remaining = this.store.sleeveApplyAmount();
    const money = Math.abs(remaining).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    if (remaining < 0) return `Remove excess ${money}`;
    return `Allocate remaining ${money}`;
  }

  /** True when the outstanding amount is an over-commitment rather than money still to place. */
  isOverCommitted(): boolean {
    return this.store.sleeveApplyAmount() < 0;
  }

  /**
   * Whether to offer the control at all — there has to be a balance to act on.
   *
   * Hidden rather than disabled when there is nothing outstanding, matching the reallocation
   * grid: a button that would change nothing is worse than no button.
   */
  showApply(): boolean {
    return this.store.hasRemainingToApply() && this.store.sleeveAmounts().rows.length > 0;
  }

  /**
   * Why the control is offered but disabled, or '' when it is usable.
   *
   * A disabled button with no reason reads as a bug — and this reason is not guessable: the
   * percentages come from Orion, and the advisor cannot see they fail to total 100 without
   * adding up the column themselves. One live account carries 5/20 against targets of 45/55.
   */
  applyDisabledReason(): string {
    if (this.store.sleeveAmountsCanApplyOrionPercent()) return '';
    if (this.store.fundingAmount() <= 0) return 'Enter an amount on the previous step first.';
    const kind = this.store.isContribution() ? 'contribution' : 'distribution';
    return `This account's ${kind} percentages do not total 100%, so they cannot divide the `
      + 'amount. Enter the amounts directly.';
  }

  /**
   * The signed gap between what has been placed and the funding amount.
   *
   * Money rather than percent, unlike the allocation grid's `deltaLabel` — the total here is a
   * dollar figure and a percentage delta beside it would be a second unit to reconcile.
   */
  deltaLabel(delta: number): string {
    const money = Math.abs(delta).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    return `(${delta > 0 ? '+' : '−'}${money})`;
  }

  /**
   * Red when over, mint when under — the same vocabulary as the allocation totals row.
   *
   * Over means the advisor has committed more than the request carries, which must be fixed
   * here. Under is a normal mid-edit state: the remainder is spent on the steps that follow.
   */
  deltaClass(delta: number): string {
    return delta > 0 ? 'text-red-dark' : 'text-mint-darkest';
  }

  isOff(delta: number): boolean {
    return Math.abs(delta) >= 0.005;
  }
}
